import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Activity, PlayCircle, Award, ClipboardCheck, RotateCcw, ArrowRight, Droplet, FlaskConical, Stethoscope, LayoutGrid } from "lucide-react";
import { BLOOD_CASES, optPts, serveCase } from "@/data/bloodReport";
import { useCaseList } from "@/context/ContentContext";
import { BeatShell, OptionList } from "@/components/nutrition/NutritionParts";
import { LabReportPanel } from "@/components/bloodreport/LabReportPanel";
import { SaveSession } from "@/components/SaveSession";
import { FurtherReading } from "@/components/FurtherReading";

const ACCENT = "#b45309";
const pick = (cases, ex) => {
  const pool = cases.filter((c) => c.id !== ex);
  const list = pool.length ? pool : cases;
  return list[Math.floor(Math.random() * list.length)];
};
const band = (p) =>
  p >= 80 ? { label: "Competent", text: "text-emerald-600", ring: "border-emerald-300 bg-emerald-50" }
  : p >= 60 ? { label: "Developing", text: "text-amber-600", ring: "border-amber-300 bg-amber-50" }
  : { label: "Not yet competent", text: "text-rose-600", ring: "border-rose-300 bg-rose-50" };

const Sig = ({ c }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 p-3.5 text-sm text-slate-700 leading-relaxed" data-testid="blood-briefing">
    <span className="font-semibold text-slate-900">{c.name}</span> <span className="text-slate-500">— {c.signalment}</span>
    <div className="mt-2">{c.briefing}</div>
  </div>
);

// Beat 1 — interpret the pattern (read-only lab report + condition choices)
function ChoiceStage({ c, beat, eyebrow, title, Icon, full, k, label, last, panel, onDone }) {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;
  const primary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose an answer.");
      setRes({ earned: optPts(beat.options[idx].score, full, Math.round(full / 2)), max: full, detail: "", key: k, label });
    } else onDone(res);
  };
  return (
    <BeatShell Icon={Icon} eyebrow={eyebrow} title={title} subtitle={beat.prompt}
      primaryLabel={checked ? last ? "See debrief →" : "Continue →" : "Check answer"} onPrimary={primary} primaryTestId={`${k}-primary-btn`}>
      {panel && (
        <>
          <Sig c={c} />
          <LabReportPanel panel={c.panel} revealed={checked} />
        </>
      )}
      <OptionList options={beat.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix={`${k}-option`} />
    </BeatShell>
  );
}

export default function BloodReport() {
  const cases = useCaseList("bloodwork", BLOOD_CASES);
  const [stage, setStage] = useState("intro");
  const [c, setC] = useState(null);
  const [beat, setBeat] = useState(0);
  const [scores, setScores] = useState([]);
  const [debrief, setDebrief] = useState(null);

  const begin = (ex) => { setC(serveCase(pick(cases, ex))); setBeat(0); setScores([]); setDebrief(null); setStage("running"); };
  const done = (s) => {
    const next = [...scores, s];
    if (beat < 1) { setScores(next); setBeat(beat + 1); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    const earned = next.reduce((a, b) => a + b.earned, 0);
    const max = next.reduce((a, b) => a + b.max, 0) || 1;
    setDebrief({ percent: Math.round((earned / max) * 100), breakdown: next, title: c.name, next: c.nextStep, nextRoute: c.nextRoute, nextLabel: c.nextLabel });
    setStage("debrief"); window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5" data-testid="blood-home-link">
            <div className="w-8 h-8 rounded-md flex items-center justify-center text-white" style={{ background: ACCENT }}>
              <Droplet className="w-4 h-4" />
            </div>
            <div>
              <div className="text-sm font-semibold tracking-tight text-slate-900">VetSim Bloodwork</div>
              <div className="text-[11px] text-slate-500 -mt-0.5">Haematology &amp; biochemistry interpretation</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            {stage === "running" && (
              <div className="flex gap-1.5" data-testid="blood-progress">
                {[0, 1].map((i) => <span key={i} className="w-5 h-1.5 rounded-full" style={{ background: i <= beat ? ACCENT : "#e2e8f0" }} />)}
              </div>
            )}
            <Link
              to="/sections"
              data-testid="blood-sections-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors whitespace-nowrap"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Simulator sections</span>
              <span className="sm:hidden">Sections</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {stage === "intro" && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm fade-up text-center" data-testid="blood-intro">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto text-white" style={{ background: ACCENT }}>
              <Droplet className="w-7 h-7" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-4">Bloodwork Interpretation</h1>
            <p className="text-base text-slate-600 mt-3 leading-relaxed max-w-2xl mx-auto">
              Read a graphical haematology &amp; biochemistry report from a real-style analyser, with the
              <span className="font-semibold text-slate-800"> abnormal results already flagged</span> against their reference
              intervals. Your job is to interpret what the pattern means and decide what to do next. Get one wrong and you'll get
              the explanation — so it sticks next time.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
              {["Reference intervals", "Flagged abnormals", "Pattern interpretation", "Teaching feedback"].map((t) => (
                <span key={t} className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded bg-slate-100 text-slate-600">{t}</span>
              ))}
            </div>
            <div className="mt-7">
              <Button onClick={() => begin(null)} data-testid="start-drill-btn" className="text-white text-base px-6 py-5" style={{ background: ACCENT }}>
                <PlayCircle className="w-5 h-5 mr-2" /> Start a case · {cases.length} patients
              </Button>
            </div>
            <FurtherReading topic="clinpath" className="mt-8 text-left" />
          </div>
        )}

        {stage === "running" && c && (
          <div data-testid="blood-running">
            {beat === 0 && <ChoiceStage c={c} beat={c.interpret} eyebrow="Stage 1 of 2 · Read &amp; interpret" title="Read the report — what's the pattern?" Icon={FlaskConical} full={60} k="interpret" label="Interpretation" panel onDone={done} />}
            {beat === 1 && <ChoiceStage c={c} beat={c.action} eyebrow="Stage 2 of 2 · Next step" title="What's the right response?" Icon={Stethoscope} full={40} k="action" label="Next step" last onDone={done} />}
          </div>
        )}

        {stage === "debrief" && debrief && (
          <div className="space-y-5 fade-up">
            <div className="bg-white border border-emerald-200 rounded-xl p-6 shadow-lg" data-testid="blood-debrief">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-semibold mb-5">
                <ClipboardCheck className="w-4 h-4" /><span>Debrief · {debrief.title}</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="flex items-center gap-5">
                  <div className={`flex flex-col items-center justify-center w-24 h-24 rounded-2xl border-4 ${band(debrief.percent).ring}`} data-testid="blood-grade">
                    <Award className={`w-5 h-5 ${band(debrief.percent).text}`} />
                    <span className={`text-4xl font-bold leading-none ${band(debrief.percent).text}`} data-testid="blood-percent">{debrief.percent}</span>
                    <span className="text-[10px] text-slate-400 font-semibold">/100</span>
                  </div>
                  <div>
                    <div className={`text-xl font-bold ${band(debrief.percent).text}`} data-testid="blood-band">{band(debrief.percent).label}</div>
                    <div className="text-xs text-slate-500 mt-1">Cert IV clinical pathology</div>
                  </div>
                </div>
                <div className="lg:col-span-2" data-testid="blood-breakdown">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Stage scores</div>
                  <div className="space-y-2">
                    {debrief.breakdown.map((b) => {
                      const pct = Math.round((b.earned / b.max) * 100);
                      return (
                        <div key={b.key} data-testid={`blood-stage-${b.key}`}>
                          <div className="flex items-center justify-between text-xs mb-0.5">
                            <span className="text-slate-700 font-medium">{b.label}</span>
                            <span className="font-mono-clinical font-semibold text-slate-800">{b.earned}/{b.max}</span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: pct >= 80 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444" }} />
                          </div>
                          {b.detail && <div className="text-[10px] text-slate-500 mt-0.5">{b.detail}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button onClick={() => begin(c.id)} data-testid="next-case-btn" className="text-white" style={{ background: ACCENT }}>
                  Next patient <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button onClick={() => setStage("intro")} variant="outline" data-testid="restart-btn" className="text-slate-600">
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Back to start
                </Button>
              </div>
            </div>
            {debrief.next && (
              <div className="bg-white border border-amber-200 rounded-xl p-5 shadow-sm" data-testid="blood-nextstep">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-amber-700 font-semibold mb-2">
                  <ArrowRight className="w-4 h-4" /><span>Suggested next test</span>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{debrief.next}</p>
                {debrief.nextRoute && (
                  <Link to={debrief.nextRoute} data-testid="blood-nextstep-link"
                    className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold rounded-md px-3.5 py-2 text-white transition-opacity hover:opacity-90"
                    style={{ background: ACCENT }}>
                    {debrief.nextLabel || "Go to next bench"} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            )}
            <SaveSession simulatorId="bloodwork" simulatorName="Bloodwork Interpretation" score={debrief.percent} grade={band(debrief.percent).label} summary={`${debrief.title} · ${band(debrief.percent).label}`} />
            <FurtherReading topic="clinpath" />
          </div>
        )}
      </main>
    </div>
  );
}
